import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ExternalLink, IdCard, LogOut, ShieldCheck, Save, RefreshCw } from "lucide-react";
import { api, useAuth } from "@/auth/AuthContext";

const STATUS_META = {
  not_submitted: { label: "Belum diisi", tone: "chip-tone-warn" },
  pending: { label: "Menunggu verifikasi", tone: "chip-tone-info" },
  verified: { label: "Terverifikasi", tone: "chip-tone-success" },
  needs_revision: { label: "Perlu diperbaiki", tone: "chip-tone-danger" },
};

export default function ProfilPengelola({ embedded = false, onSubmitted }) {
  const { user, logout, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ nama_pengelola: "", jabatan_pengelola: "", nomor_wa: "", email: "", password: "", password_confirm: "", sk_link: "" });
  const [showForm, setShowForm] = useState(false);
  const [checkingUrl, setCheckingUrl] = useState(false);
  const [urlOk, setUrlOk] = useState(null); // null | true | false
  const [urlMsg, setUrlMsg] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/pt/profile");
      setProfile(r.data);
      // Prefill form (empty password fields)
      setForm((f) => ({
        ...f,
        nama_pengelola: r.data.nama_pengelola || "",
        jabatan_pengelola: r.data.jabatan_pengelola || "",
        nomor_wa: r.data.nomor_wa || "",
        email: r.data.email || "",
        sk_link: r.data.sk_link || "",
        password: "", password_confirm: "",
      }));
      // Auto open form if not verified
      const status = r.data.status;
      if (status === "not_submitted" || status === "needs_revision") setShowForm(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal memuat profil.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const checkUrl = async () => {
    if (!form.sk_link.trim()) { setUrlOk(null); setUrlMsg(""); return; }
    setCheckingUrl(true); setUrlOk(null); setUrlMsg("");
    try {
      const r = await api.post("/util/check-url", { url: form.sk_link.trim() });
      setUrlOk(r.data.ok);
      setUrlMsg(r.data.ok ? "Tautan dapat diakses publik." : r.data.reason || "Tautan tidak dapat diakses publik.");
    } catch (e) {
      setUrlOk(false);
      setUrlMsg(e.response?.data?.detail || "Gagal memeriksa tautan.");
    } finally {
      setCheckingUrl(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice("");
    if (form.password !== form.password_confirm) {
      setError("Konfirmasi password tidak cocok.");
      return;
    }
    if (form.password.length < 4) {
      setError("Password minimal 4 karakter.");
      return;
    }
    if (urlOk !== true) {
      setError("Silakan klik 'Periksa Tautan' terlebih dulu dan pastikan valid.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        nama_pengelola: form.nama_pengelola.trim(),
        jabatan_pengelola: form.jabatan_pengelola.trim(),
        nomor_wa: form.nomor_wa.trim(),
        email: form.email.trim(),
        password: form.password,
        password_confirm: form.password_confirm,
        sk_link: form.sk_link.trim(),
      };
      const r = await api.post("/pt/profile", payload);
      setProfile(r.data);
      setNotice("Data pengelola berhasil dikirim. Menunggu verifikasi admin.");
      setShowForm(false);
      setForm((f) => ({ ...f, password: "", password_confirm: "" }));
      await refresh();
      onSubmitted && onSubmitted(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal menyimpan.");
    } finally {
      setBusy(false);
    }
  };

  if (error && !profile) return <div className="loading-screen" data-testid="profile-error">{error}</div>;
  if (!profile) return <div className="loading-screen">Memuat data pengelola…</div>;

  const statusMeta = STATUS_META[profile.status] || STATUS_META.not_submitted;
  const canReadOnly = profile.status === "pending" || profile.status === "verified";

  return (
    <div className={embedded ? "" : "pt-shell"} data-testid="profil-pengelola-page">
      {!embedded && (
        <header className="pt-header">
          <div className="pt-header-brand">
            <img src="/logo-kemdikbud.png" alt="Logo Kemendikbud" className="login-logo" />
            <div>
              <div className="eyebrow">PROFIL PENGELOLA</div>
              <h1 data-testid="profil-title">{profile.namapt || user?.display_name}</h1>
              <p className="subtitle">Kode PT: <span className="mono">{profile.kodept || user?.kodept}</span></p>
            </div>
          </div>
          <div className="pt-header-actions">
            <button className="download-button ghost" data-testid="profil-logout" onClick={logout}><LogOut size={14} /> Keluar</button>
          </div>
        </header>
      )}

      <section className="pt-card">
        <div className="eyebrow"><IdCard size={13} /> STATUS VERIFIKASI</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
          <div>
            <h2 style={{ marginBottom: 6 }}>Data Pengelola KIPK</h2>
            <p className="muted-text" style={{ fontSize: 12 }}>Perbarui data pengelola secara berkala untuk memastikan komunikasi berjalan lancar.</p>
          </div>
          <span className={`folder-chip ${statusMeta.tone}`} data-testid="profil-status" style={{ pointerEvents: "none", padding: "8px 14px", fontSize: 12 }}>
            {statusMeta.label}
          </span>
        </div>

        {profile.status === "needs_revision" && profile.revision_notes && (
          <div className="error-banner" data-testid="profil-revision-notes" style={{ marginTop: 14 }}>
            <AlertTriangle size={16} /> <div><strong>Catatan Admin:</strong> {profile.revision_notes}</div>
          </div>
        )}
        {profile.status === "pending" && (
          <div className="info-banner" data-testid="profil-pending-banner" style={{ marginTop: 14 }}>
            <CheckCircle2 size={16} /> Data Anda sedang menunggu verifikasi admin. Silakan cek berkala.
          </div>
        )}
        {profile.status === "verified" && (
          <div className="info-banner" data-testid="profil-verified-banner" style={{ marginTop: 14 }}>
            <ShieldCheck size={16} /> Data pengelola telah terverifikasi pada {profile.verified_at?.slice(0, 10) || "—"}.
          </div>
        )}
      </section>

      {canReadOnly && !showForm && (
        <section className="pt-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <h2>Rincian Pengelola</h2>
            <button className="download-button ghost" data-testid="profil-edit" onClick={() => setShowForm(true)}>
              <RefreshCw size={14} /> Perbarui Data
            </button>
          </div>
          <div className="pt-identity-grid" style={{ marginTop: 14 }}>
            <div><span>Nama Pengelola</span><strong data-testid="profil-nama">{profile.nama_pengelola || "—"}</strong></div>
            <div><span>Jabatan Pengelola</span><strong>{profile.jabatan_pengelola || "—"}</strong></div>
            <div><span>Nomor WhatsApp</span><strong className="mono">{profile.nomor_wa || "—"}</strong></div>
            <div><span>Alamat Email</span><strong className="mono">{profile.email || "—"}</strong></div>
            <div style={{ gridColumn: "span 2" }}>
              <span>Tautan Dokumen SK Pengelola</span>
              {profile.sk_link ? (
                <a href={profile.sk_link} target="_blank" rel="noreferrer" className="folder-chip chip-tone-info" data-testid="profil-sk-link" style={{ marginTop: 6, width: "fit-content" }}>
                  <ExternalLink size={12} /> Buka dokumen
                </a>
              ) : <strong>—</strong>}
            </div>
          </div>
        </section>
      )}

      {(showForm || profile.status === "not_submitted" || profile.status === "needs_revision") && (
        <section className="pt-card">
          <div className="eyebrow"><IdCard size={13} /> ISIAN DATA</div>
          <h2>{profile.status === "not_submitted" ? "Lengkapi data pengelola" : "Perbarui data pengelola"}</h2>
          <p className="muted-text" style={{ fontSize: 12, marginBottom: 14 }}>Wajib diisi. Password lama akan diganti dengan password baru setelah Anda simpan.</p>

          {notice && <div className="info-banner" data-testid="profil-notice">✓ {notice}</div>}
          {error && <div className="error-banner" data-testid="profil-form-error"><AlertTriangle size={14} /> {error}</div>}

          <form onSubmit={submit} className="acct-form" data-testid="profil-form">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label><span>Nama Pengelola</span><input data-testid="profil-nama-input" value={form.nama_pengelola} onChange={(e) => set("nama_pengelola", e.target.value)} required /></label>
              <label><span>Jabatan Pengelola</span><input data-testid="profil-jabatan-input" value={form.jabatan_pengelola} onChange={(e) => set("jabatan_pengelola", e.target.value)} required /></label>
              <label><span>Nomor WhatsApp</span><input data-testid="profil-wa-input" value={form.nomor_wa} onChange={(e) => set("nomor_wa", e.target.value)} placeholder="+62812xxxx" required /></label>
              <label><span>Alamat Email</span><input type="email" data-testid="profil-email-input" value={form.email} onChange={(e) => set("email", e.target.value)} required /></label>
              <label><span>Password Baru</span><input type="password" data-testid="profil-password-input" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={4} autoComplete="new-password" /></label>
              <label><span>Konfirmasi Password Baru</span><input type="password" data-testid="profil-password-confirm-input" value={form.password_confirm} onChange={(e) => set("password_confirm", e.target.value)} required minLength={4} autoComplete="new-password" /></label>
            </div>
            <label>
              <span>Tautan Dokumen SK Pengelola</span>
              <div style={{ display: "flex", gap: 8 }}>
                <input data-testid="profil-sk-input" style={{ flex: 1 }} value={form.sk_link} onChange={(e) => { set("sk_link", e.target.value); setUrlOk(null); setUrlMsg(""); }} placeholder="https://drive.google.com/..." required />
                <button type="button" className="download-button ghost" data-testid="profil-sk-check" onClick={checkUrl} disabled={checkingUrl || !form.sk_link.trim()}>
                  {checkingUrl ? "Memeriksa…" : "Periksa Tautan"}
                </button>
              </div>
              {urlOk === true && <span style={{ color: "var(--green)", fontSize: 11, marginTop: 4, display: "inline-flex", gap: 6, alignItems: "center" }}><CheckCircle2 size={12} /> {urlMsg}</span>}
              {urlOk === false && <span style={{ color: "var(--red)", fontSize: 11, marginTop: 4, display: "inline-flex", gap: 6, alignItems: "center" }}><AlertTriangle size={12} /> {urlMsg}</span>}
              <span className="muted-text" style={{ fontSize: 11, marginTop: 4 }}>Ubah pengaturan berbagi Google Drive menjadi "Anyone with the link" agar dapat diakses.</span>
            </label>

            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button type="submit" className="download-button" data-testid="profil-submit" disabled={busy}>
                <Save size={14} /> {busy ? "Menyimpan…" : "Simpan & Kirim untuk Verifikasi"}
              </button>
              {canReadOnly && (
                <button type="button" className="download-button ghost" data-testid="profil-cancel" onClick={() => { setShowForm(false); setError(""); }}>
                  Batal
                </button>
              )}
            </div>
          </form>
        </section>
      )}
    </div>
  );
}
